import { useEffect, useState } from "react";
import { Save, Plus, Pencil, Trash2, FileText, CircleHelp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_FAQ = { question: "", answer: "", order: 0, published: true };

function TextsTab() {
  const [blocks, setBlocks] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/content").then((r) => {
      setBlocks(r.data);
      setValues(Object.fromEntries(r.data.map((b) => [b.key, b.value])));
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { items: blocks.map((b) => ({ key: b.key, label: b.label, value: values[b.key] ?? "", multiline: b.multiline })) });
      toast.success("Contenu enregistré");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!blocks) return <p className="text-muted-foreground">Chargement…</p>;

  return (
    <div className="space-y-5" data-testid="content-blocks">
      {blocks.map((b) => (
        <div key={b.key} className="bg-white border border-line p-5">
          <Label className="font-semibold">{b.label}</Label>
          <p className="text-xs text-muted-foreground font-mono mt-0.5 mb-2">{b.key}</p>
          {b.multiline ? (
            <Textarea rows={3} data-testid={`content-${b.key}`} value={values[b.key] ?? ""} onChange={(e) => setValues({ ...values, [b.key]: e.target.value })} />
          ) : (
            <Input data-testid={`content-${b.key}`} value={values[b.key] ?? ""} onChange={(e) => setValues({ ...values, [b.key]: e.target.value })} />
          )}
        </div>
      ))}
      <button onClick={save} disabled={saving} data-testid="content-save"
        className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer les textes"}
      </button>
    </div>
  );
}

function FaqTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/faq-items").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/faq-items/${editing.id}`, payload);
      else await api.post("/admin/faq-items", payload);
      toast.success("Question enregistrée");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    await api.delete(`/admin/faq-items/${id}`);
    load();
  };

  if (!items) return <p className="text-muted-foreground">Chargement…</p>;

  return (
    <div data-testid="faq-admin">
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...EMPTY_FAQ })} data-testid="faq-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Ajouter une question
        </button>
      </div>
      <div className="space-y-3">
        {items.map((f) => (
          <div key={f.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`faq-row-${f.id}`}>
            <CircleHelp className="w-5 h-5 text-rouge shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <p className="font-display font-bold text-onyx text-sm">{f.question} {!f.published && <span className="text-xs font-sans text-muted-foreground">(masquée)</span>}</p>
            </div>
            <button onClick={() => setEditing({ ...f })} data-testid={`faq-edit-${f.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(f.id)} data-testid={`faq-delete-${f.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white" data-testid="faq-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Ajouter"} une question</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Question *</Label><Input data-testid="faq-question-input" className="mt-1.5" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} /></div>
              <div><Label>Réponse *</Label><Textarea rows={4} data-testid="faq-answer-input" className="mt-1.5" value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} /></div>
              <div><Label>Ordre d'affichage</Label><Input type="number" data-testid="faq-order-input" className="mt-1.5" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <div className="flex items-center gap-3">
                <Switch data-testid="faq-published-switch" checked={!!editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                <Label>Publiée</Label>
              </div>
              <button onClick={save} disabled={saving || !editing.question || !editing.answer} data-testid="faq-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ContentAdmin() {
  return (
    <div data-testid="admin-content">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx mb-8">Contenu du site</h1>
      <Tabs defaultValue="texts">
        <TabsList className="bg-white border border-line mb-6">
          <TabsTrigger value="texts" data-testid="tab-texts"><FileText className="w-4 h-4 mr-2" /> Textes du site</TabsTrigger>
          <TabsTrigger value="faq" data-testid="tab-faq"><CircleHelp className="w-4 h-4 mr-2" /> FAQ</TabsTrigger>
        </TabsList>
        <TabsContent value="texts"><TextsTab /></TabsContent>
        <TabsContent value="faq"><FaqTab /></TabsContent>
      </Tabs>
    </div>
  );
}
